import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Store, Building2, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const OPTIONS = [
  { v: "owner", l: "Owner", desc: "I'm selling my own property / vehicle / business.", icon: Building2 },
  { v: "broker", l: "Agent", desc: "I help others buy & sell. I list on their behalf.", icon: Users },
];

export default function BecomeSeller() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [pick, setPick] = useState((user && user.sellerType) || "owner");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!user) { navigate("/login", { state: { from: "/become-seller" } }); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/become-seller", { sellerType: pick });
      updateUser(data);
      toast.success("You can now post listings!");
      navigate("/dashboard/create-listing");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch to seller mode.");
    } finally { setLoading(false); }
  };

  const alreadySeller = user && (user.role === "seller" || user.role === "admin");

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="become-seller-page">
      <Seo title="Start Selling — The Pataudi" />
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl bg-card rounded-card shadow-soft border border-border p-7">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
              <Store className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl text-ink">Start Selling on The Pataudi</h1>
              <p className="text-sm text-muted-foreground">Tell us what you are — we&apos;ll set up your seller account.</p>
            </div>
          </div>

          {alreadySeller && (
            <div className="mt-4 text-sm bg-success/10 text-success rounded-md px-3 py-2" data-testid="already-seller-notice">
              You&apos;re already a seller. Continue posting listings from your dashboard.
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {OPTIONS.map((o) => {
              const Icon = o.icon;
              const active = pick === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setPick(o.v)}
                  data-testid={`seller-type-${o.v}`}
                  className={`text-left rounded-md border p-4 transition-colors ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/40 bg-white"}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                    <span className={`font-display font-semibold ${active ? "text-primary" : "text-ink"}`}>{o.l}</span>
                  </div>
                  <p className="text-xs text-ink/60 mt-1.5">{o.desc}</p>
                </button>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground mt-4">
            Listings are reviewed by our admin team within 24 hours and go live once approved.
            Your contact details are visible to interested buyers (contacts stay locked until they buy a plan).
          </p>

          <Button onClick={submit} disabled={loading} className="w-full mt-5 bg-accent hover:bg-accent/90 text-white rounded-sm h-11" data-testid="become-seller-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (alreadySeller ? "Update my type & continue" : "Continue as Seller")}
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
